import { useCallback } from 'react';
import { pick, prop } from 'ramda';
import { languageService } from 'js/services/language.service';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';

import * as Types from './types';
import { useGenericSend } from '../useGenericSend';

export const useGetURLTemplates = () => {
    const send = useGenericSend();
    const appClientPlatform = useDeviceSelector(prop('clientPlatform'));
    const { lang, userDetails: { creferer: appCreferer } } = useUserSelector(pick(['lang', 'userDetails']));
    const { casinoName: appCasinoName, clientSkin: appClientSkin, clientType: appClientType }
        = useAppSelector(pick(['casinoName', 'clientSkin', 'clientType']));

    return useCallback(async (
        {
            creferer = appCreferer,
            language = lang,
            clientType = appClientType,
            casinoName = appCasinoName,
            clientSkin = appClientSkin,
            urlTypeList,
            clientPlatform = appClientPlatform,
            customParameters
        }: Types.IGetURLTemplatesParams,
        options?: { signal?: AbortSignal }
    ): Promise<Types.IGetURLsNewResponseParams> => {
        const { data } = await send<Types.IGetURLsNewRequestParams, Types.IGetURLsNewResponseParams>({
            ID: 35844,
            responses: [31032, 31033],
            description: 'Get URLs Request',
            creferer,
            clientType,
            casinoName,
            urlTypeList,
            languageCode: languageService.convertToIMS(language),
            clientSkinName: clientSkin,
            clientPlatform,
            customParameters
        }, options);

        return data;
    }, [send, appCasinoName, appClientSkin, appClientPlatform, appClientType, lang, appCreferer]);
};

export const useGetURLTemplate = () => {
    const getURLTemplates = useGetURLTemplates();

    return useCallback(async (
        {
            priority,
            creferer,
            language,
            clientType,
            casinoName,
            clientSkin,
            urlTypeList,
            clientPlatform,
            customParameters
        }: Types.IGetURLTemplateParams,
        options?: { signal?: AbortSignal }
    ): Promise<string | undefined> => {
        const { urlList = [] } = await getURLTemplates({
            creferer,
            clientType,
            casinoName,
            urlTypeList,
            language,
            clientSkin,
            clientPlatform,
            customParameters
        }, options);
        const sortedUrlList = urlList.sort((a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity));
        const highestPriorityUrl = sortedUrlList[0]?.url;

        if (typeof priority === 'number') {
            return sortedUrlList.filter((item) => item.priority === priority)[0]?.url || highestPriorityUrl;
        }

        return highestPriorityUrl;
    }, [
        getURLTemplates
    ]);
};
